/**
 * Tracks which comments arrived via SSE *after* the panel was opened.
 *
 * Simple strategy — no localStorage, no watermarks:
 *  1. On first data load, snapshot all existing comment IDs
 *  2. On subsequent refetches (SSE-triggered), any NEW IDs = unread
 *  3. `markAllRead()` clears the unread set
 *  4. Unread comments get the pulse glow animation
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import type { NegotiationComment } from '../services/negotiationsApi'

export interface UnreadState {
  /** Is a specific comment unread (arrived via SSE)? */
  isUnread: (commentId: number) => boolean
  /** Total unread count */
  unreadCount: number
  /** Unread reply count for a specific root comment */
  unreadReplyCountFor: (rootCommentId: number) => number
  /** IDs of new comments (for toast) */
  newIds: number[]
  /** Clear all unread state */
  markAllRead: () => void
}

export function useUnreadComments(
  negotiationId: number,
  comments: NegotiationComment[] | undefined,
  actorId?: string,
): UnreadState {
  // Set of IDs we knew about on first load
  const knownIdsRef = useRef<Set<number> | null>(null)
  // Set of IDs that arrived after first load
  const [newIds, setNewIds] = useState<number[]>([])

  // Flatten all comments (root + replies) with author info for filtering
  const allEntries = useMemo(() => {
    if (!comments) return []
    const entries: { id: number; authorId?: string }[] = []
    for (const c of comments) {
      entries.push({ id: c.id, authorId: c.author?.koblink_id })
      if (c.replies) {
        for (const r of c.replies) {
          entries.push({ id: r.id, authorId: r.author?.koblink_id })
        }
      }
    }
    return entries
  }, [comments])

  const allIds = useMemo(() => allEntries.map((e) => e.id), [allEntries])

  // On negotiation change, reset everything
  useEffect(() => {
    knownIdsRef.current = null
    setNewIds([])
  }, [negotiationId])

  // Track new arrivals
  useEffect(() => {
    if (!allIds.length) return

    // First load — snapshot all existing IDs as "known"
    if (knownIdsRef.current === null) {
      knownIdsRef.current = new Set(allIds)
      return
    }

    // Subsequent loads — find IDs we haven't seen before, excluding own comments
    const known = knownIdsRef.current
    const authorById = new Map(allEntries.map((e) => [e.id, e.authorId]))
    const arrivals = allIds.filter(
      (id) => !known.has(id) && authorById.get(id) !== actorId,
    )

    if (arrivals.length > 0) {
      // Add to known so we don't re-detect them
      for (const id of arrivals) {
        known.add(id)
      }
      setNewIds((prev) => {
        const combined = new Set([...prev, ...arrivals])
        return Array.from(combined)
      })
    }
  }, [allIds, allEntries, actorId])

  const newIdSet = useMemo(() => new Set(newIds), [newIds])

  const isUnread = useCallback(
    (commentId: number) => newIdSet.has(commentId),
    [newIdSet],
  )

  const unreadCount = newIds.length

  const unreadReplyCountFor = useCallback(
    (rootCommentId: number) => {
      const root = comments?.find((c) => c.id === rootCommentId)
      if (!root?.replies) return 0
      return root.replies.filter((r: NegotiationComment) => newIdSet.has(r.id)).length
    },
    [comments, newIdSet],
  )

  const markAllRead = useCallback(() => {
    setNewIds([])
  }, [])

  return {
    isUnread,
    unreadCount,
    unreadReplyCountFor,
    newIds,
    markAllRead,
  }
}
